import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'

/**
 * Colour alone never carries the meaning here — the label does — so a badge stays readable in
 * greyscale, on a projector, and to a colour-blind reviewer.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'border-neutral-border bg-neutral-subtle text-body',
        brand: 'border-transparent bg-brand-fill-subtle text-brand-text',
        success: 'border-success-border bg-success-subtle text-success-text',
        warning: 'border-warning-border bg-warning-subtle text-warning-text',
        danger: 'border-danger-border bg-danger-subtle text-danger-text',
        solid: 'border-transparent bg-brand-fill text-on-brand-fill',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  },
)

export type BadgeProps = ComponentProps<'span'> & VariantProps<typeof badgeVariants>

export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />
}

export { badgeVariants }
